import { LitElement, html, css } from 'lit';
import { API_BASE_URL } from './utils.js';

export class ChangeDetectorTab extends LitElement {
    static properties = {
        contextManager: { type: Object },
        activeContextId: { type: String },
        isRunning: { state: true },
        lastActivationTimestamp: { state: true },
        rules: { state: true },
        statusCheckInterval: { state: true }
    };

    static styles = css`
        :host {
            display: block;
            padding: 20px;
        }

        .change-detector-container {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .status-row {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .rules-status-container {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        .rule-status {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .no-context-message {
            text-align: center;
            padding: 40px;
            background-color: var(--spectrum-gray-100);
            border-radius: 8px;
            border: 1px solid var(--spectrum-gray-300);
        }
    `;

    constructor() {
        super();
        this.contextManager = null;
        this.activeContextId = null;
        this.isRunning = false;
        this.lastActivationTimestamp = null;
        this.rules = [];
        this.statusCheckInterval = null;
    }

    connectedCallback() {
        super.connectedCallback();
        this.startStatusChecking();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.stopStatusChecking();
    }

    updated(changedProperties) {
        if (changedProperties.has('activeContextId')) {
            // Reset status when context changes
            this.isRunning = false;
            this.lastActivationTimestamp = null;
            this.rules = [];
            this.checkChangeDetectorStatus();
        }
    }

    getActiveCredentials() {
        return this.contextManager?.getContextCredentials();
    }

    startStatusChecking() {
        this.checkChangeDetectorStatus();
        this.statusCheckInterval = setInterval(() => {
            this.checkChangeDetectorStatus();
        }, 2000);
    }

    stopStatusChecking() {
        if (this.statusCheckInterval) {
            clearInterval(this.statusCheckInterval);
            this.statusCheckInterval = null;
        }
    }

    async checkChangeDetectorStatus() {
        const credentials = this.getActiveCredentials();
        if (!credentials) {
            return;
        }

        const { aemAdminToken, aioAuth, aioNamespace } = credentials;

        if (!aemAdminToken || !aioAuth || !aioNamespace) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/change-detector`, {
                method: 'GET',
                headers: {
                    'X-AEM-admin-token': aemAdminToken,
                    'X-AIO-auth': aioAuth,
                    'X-AIO-namespace': aioNamespace
                }
            });

            const data = await response.json();
            console.log('Change detector data:', data);

            this.isRunning = data.running || false;
            this.lastActivationTimestamp = data.lastActivationTimestamp;
            this.rules = data.rules || [];
        } catch (error) {
            console.error('Error checking change detector status:', error);
        }
    }

    async setRuleStatus(ruleName, active) {
        const credentials = this.getActiveCredentials();
        if (!credentials) {
            console.error('No active context credentials');
            return;
        }

        const { aioAuth, aioNamespace } = credentials;

        if (!aioAuth || !aioNamespace) {
            console.error('Missing required credentials');
            return;
        }

        console.log(`Setting rule ${ruleName} to ${active ? 'active' : 'inactive'}`);

        try {
            const response = await fetch(`${API_BASE_URL}/change-detector/rule`, {
                method: 'POST',
                headers: {
                    'X-AIO-auth': aioAuth,
                    'X-AIO-namespace': aioNamespace,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ruleName,
                    active
                })
            });

            const data = await response.json();
            console.log('Rule status set:', data);

            // Refresh status immediately
            await this.checkChangeDetectorStatus();
        } catch (error) {
            console.error('Error setting rule status:', error);
        }
    }

    getStatusText() {
        if (this.isRunning) {
            return 'Change detection in progress...';
        }
        if (this.lastActivationTimestamp) {
            return `Last checked ${new Date(this.lastActivationTimestamp).toLocaleString()}`;
        }
        return 'Status unknown';
    }

    getStatusVariant() {
        if (this.isRunning) {
            return 'negative';
        }
        if (this.lastActivationTimestamp) {
            return 'positive';
        }
        return 'neutral';
    }

    renderRule(rule) {
        return html`
            <div class="rule-status">
                <overlay-trigger triggered-by="hover">
                    <sp-status-light 
                        slot="trigger"
                        size="m"
                        variant="${rule?.active ? 'positive' : 'negative'}">
                        ${rule?.name}
                    </sp-status-light>
                    <sp-tooltip slot="hover-content" placement="bottom">
                        ${rule?.description || 'No description available'}
                    </sp-tooltip>
                </overlay-trigger>
                <sp-button 
                    size="s"
                    @click="${() => this.setRuleStatus(rule.id, !rule.active)}">
                    ${rule.active ? 'Stop' : 'Start'}
                </sp-button>
            </div>
        `;
    }

    render() {
        const credentials = this.getActiveCredentials();
        
        if (!credentials) {
            return html`
                <div class="no-context-message">
                    <h3>No Context Selected</h3>
                    <p>Please select a context from the dropdown above or create a new one in the Settings tab.</p>
                </div>
            `;
        }

        return html`
            <div class="change-detector-container">
                <span>Detected product changes only affect PDP rendering.</span>
                <div class="status-row">
                    <overlay-trigger triggered-by="hover">
                        <sp-status-light 
                            slot="trigger"
                            size="m"
                            variant="${this.getStatusVariant()}">
                            ${this.getStatusText()}
                        </sp-status-light>
                        <sp-tooltip slot="hover-content" placement="bottom">
                            Last time the cycle for detecting changes in product in Catalog Service was run.
                        </sp-tooltip>
                    </overlay-trigger>
                </div>

                ${this.rules.length > 0 ? html`
                    <div class="rules-status-container">
                        ${this.rules.map(rule => this.renderRule(rule))}
                    </div>
                ` : ''}
            </div>
        `;
    }
}

// Only define the custom element if it hasn't been defined already
if (!customElements.get('change-detector-tab')) {
    customElements.define('change-detector-tab', ChangeDetectorTab);
} 